/**
 * Customer-scoped layout component that seeds the Redux customer context
 * and renders its children (via React Router Outlet) once the customer
 * data is available.
 *
 * Sits inside a RequireAccess (or RequireAuth) route wrapper and is the
 * direct replacement for the CustomerHOC / BillboardAppHOC customer-tier
 * data layer. Compose it as the element on a customer-scoped layout route,
 * with child routes rendered via <Outlet />.
 *
 * Contains:
 * - CustomerLayout — customer-tier layout route component
 */
import React, { useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import ScopeContext from '@sophocles/app/ScopeContext';
import RouteLoader from '@sophocles/components/App/RouteLoader';
import ScopeLoadError from '@sophocles/components/Layout/ScopeLoadError';
import { resolveScopeLoadState, SCOPE_LOAD_STATE } from '@sophocles/components/Layout/scopeLoadState';
import useCustomer from '@sophocles/hooks/useCustomer';

/**
 * @param {{ skipEntityFetches?: boolean }} props
 */
function CustomerLayout({ skipEntityFetches = false, ...props }) {
  const { cust_id: custId } = useParams();
  const { customer, loading: customerLoading, error: customerError, reload } = useCustomer(custId);

  const loading = customerLoading;

  // Convert the legacy immutable customer to plain JS at this single boundary so
  // descendant views (via useScope) never touch immutable.js.
  const scope = useMemo(() => ({ customer: customer ? customer.toJS() : null }), [customer]);

  // Hold rendering until the customer id in the store matches the URL,
  // so descendant views never render against a stale customer.
  const customerId = customer.get('id', '0').toString();
  const isReady = customerId === String(custId);

  const loadState = resolveScopeLoadState({ loading, error: customerError, ready: isReady });
  if (loadState === SCOPE_LOAD_STATE.ERROR) {
    return <ScopeLoadError onRetry={reload} />;
  }
  if (loadState === SCOPE_LOAD_STATE.LOADING) {
    return <RouteLoader showShell />;
  }

  return (
    <ScopeContext.Provider value={scope}>
      <Outlet />
    </ScopeContext.Provider>
  );
}

export default CustomerLayout;
